import {
  addBranch,
  AmountField,
  countryName,
  Dialog,
  formatWhole,
  getMe,
  getPeople,
  InvalidInput,
  Pick,
  setKin,
  type Family,
  type Kin,
  type KinStop,
  type Member,
  type Plan,
  type PlanStop,
} from '@trippath/shared';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { dayShort, problemKey } from '../../lib/format';
import { ModeIcon } from '../icons/ModeIcon';
import { PickedPath } from '../stop/PickedPath';
import { StopDetail } from '../stop/StopDetail';
import { WayDetail } from '../way/WayDetail';
import { useReadOnly } from '../../app/readOnly';

/** How many colours a family's trips take in turn. */
export const KINS = 6;

/** Each trip of a family by its place in it: its colour, how deep it sits in the tree. */
export function kinOf(family: Family, id: string): { kin: number; depth: number } {
  const find = (key: string | null) => family.trips.find((trip) => trip.id === key);
  const depth = (trip: Kin | undefined): number =>
    trip?.parent ? 1 + depth(find(trip.parent)) : 0;
  const trip = find(id);
  // By its place in the whole family, the same here as on the trips list.
  return { kin: (trip?.place ?? 0) % KINS, depth: depth(trip) };
}

/** Days from one day to another, `2027-03-22` to `2027-03-29` is 7. */
function between(from: string, to: string): number {
  return Math.round(
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000,
  );
}

/** What does not fit where a trip meets another: days unplanned before, or arriving too late. */
function useMeetNote() {
  const { t } = useTranslation();
  return (trip: Kin) => {
    const stop = trip.meets?.stop;
    if (!stop) return null;
    const gap = between(trip.end, stop.start);
    if (gap > 0) return t('family.meetGap', { count: gap, city: stop.city });
    return trip.end > stop.end ? t('family.meetLate', { city: stop.city }) : null;
  };
}

/** The day a trip joins the one it meets: when that one's stop begins, or it ends, the later. */
function joinsOn(start: string, end: string): string {
  return start > end ? start : end;
}

/**
 * The trips that branch off `id`, and theirs: what a trip's rail and timeline show of its family.
 * Its parent and its parent's other branches are another trip's to show; the trip tree shows all.
 */
export function branchesOf(family: Family, id: string, seen: readonly string[] = [id]): Kin[] {
  // Each trip once, whatever the data says: a trip its own parent is no branch of itself.
  return family.trips
    .filter((trip) => trip.parent === id && !seen.includes(trip.id))
    .flatMap((trip) => [trip, ...branchesOf(family, trip.id, [...seen, trip.id])]);
}

/** The trips a trip parts from, its parent first, each once whatever the data says. */
export function parentsOf(family: Family, id: string, seen: readonly string[] = [id]): Kin[] {
  const of = family.trips.find((trip) => trip.id === id)?.parent;
  const parent = family.trips.find((trip) => trip.id === of && !seen.includes(trip.id));
  return parent ? [parent, ...parentsOf(family, parent.id, [...seen, parent.id])] : [];
}

/** The town where a trip joins another again, its last way going on there; none when it does not. */
export const meetsCity = (family: Family, id: string) =>
  family.trips.find((trip) => trip.id === id)?.meets?.stop?.city ?? null;

/**
 * The way a trip's people go, as the map draws it: from where it parts, its own stops, and on with
 * the trip it joins from the stop they join it, as the rail lights it.
 */
export function kinWay(family: Family, trip: Kin): Kin['stops'] {
  const from = family.trips
    .flatMap((other) => other.stops)
    .filter((stop) => stop.id === trip.partsAt?.id);
  const meet = trip.meets?.stop?.id;
  const on = family.trips.flatMap((other) => {
    const at = other.stops.findIndex((stop) => stop.id === meet);
    return at < 0 ? [] : other.stops.slice(at);
  });
  return [...from, ...trip.stops, ...on];
}

/** `22–29 Mar`: the days a stop or a trip takes. */
function days(start: string, end: string, lang: string): string {
  return `${dayShort(start, lang)} – ${dayShort(end, lang)}`;
}

interface DialogProps {
  plan: Plan;
  /** The stop picked, where the branch parts unless another is chosen. */
  at: string | null;
  onClose: () => void;
}

/**
 * A branch of the trip (the Branches mockup, §1): a trip of its own, with its own name, budget and
 * people, that parts at a stop of this one. Made, it is opened.
 */
export function BranchDialog({ plan, at, onClose }: DialogProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const ids = useId();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [partsAt, setPartsAt] = useState(at ?? plan.stops.at(-1)?.id ?? null);
  const [budget, setBudget] = useState<number | null>(null);
  const [people, setPeople] = useState<Member[]>([]);
  const [going, setGoing] = useState<string[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  // The trip's people but the one making it, who goes anyway.
  useEffect(() => {
    Promise.all([getPeople(plan.trip.id), getMe()]).then(
      ([found, me]) => {
        setPeople(found.members.filter((member) => member.userId !== me.id));
      },
      () => undefined,
    );
  }, [plan.trip.id]);

  const save = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setTried(true);
    if (name.trim() === '' || partsAt === null || budget === null) return;
    setFailed(null);
    addBranch(plan.trip.id, { name, partsAt, budget, people: going }).then(
      (made) => {
        onClose();
        void navigate(`/trips/${made.trip.id}`);
      },
      (error: unknown) => {
        const [first] = error instanceof InvalidInput ? error.fields : [];
        setFailed(t(first ? problemKey(first.problem) : 'plan.failed'));
      },
    );
  };

  return (
    <Dialog open title={t('family.newTitle', { name: plan.trip.name })} onClose={onClose}>
      <form className="form" onSubmit={save} noValidate>
        <p className="quiet">{t('family.newHint')}</p>
        <div className="field">
          <label className="field-label" htmlFor={`${ids}-name`} data-required>
            {t('family.name')}
          </label>
          <input
            id={`${ids}-name`}
            className="text-input"
            type="text"
            autoComplete="off"
            value={name}
            aria-invalid={tried && name.trim() === ''}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          {tried && name.trim() === '' && (
            <p className="field-error">{t('problem.required')}</p>
          )}
        </div>
        <div className="form-row">
          <Pick
            label={t('family.partsAt')}
            required
            options={plan.stops.map((stop) => ({
              value: stop.id,
              label: `${stop.city} · ${days(stop.start, stop.end, lang)}`,
            }))}
            value={partsAt}
            onChange={setPartsAt}
          />
          <AmountField
            label={t('family.budget')}
            required
            value={budget}
            onChange={setBudget}
            currency={plan.trip.currency}
            error={tried && budget === null ? t('problem.required') : undefined}
          />
        </div>
        {people.length > 0 && (
          <fieldset className="family-people">
            <legend className="field-label">{t('family.people')}</legend>
            {people.map((member) => (
              <label key={member.userId} className="check">
                <input
                  type="checkbox"
                  checked={going.includes(member.userId)}
                  onChange={(event) => {
                    const on = event.target.checked;
                    setGoing((was) =>
                      on ? [...was, member.userId] : was.filter((id) => id !== member.userId),
                    );
                  }}
                />
                {member.name}
              </label>
            ))}
          </fieldset>
        )}
        {failed !== null && (
          <p className="field-error" role="alert">
            {failed}
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" className="button" onClick={onClose}>
            {t('dialog.cancel')}
          </button>
          <button type="submit" className="button button-primary">
            {t('family.make')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

interface CardProps {
  plan: Plan;
  family: Family;
  onFamily: (family: Family) => void;
  /** Makes a branch of this trip. */
  onBranch: () => void;
}

/**
 * The trip's family (the Branches mockup, §1): the trip its people set out on, the branches it
 * parts into and theirs, each a trip of its own; where they part and where they meet again. This
 * trip says where it meets another, and whether its other branches see it.
 */
export function FamilyCard({ plan, family, onFamily, onBranch }: CardProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const ids = useId();
  const [failed, setFailed] = useState(false);
  const me = family.trips.find((trip) => trip.id === plan.trip.id);
  const meetNote = useMeetNote();
  // A trip read only: its family said, where it meets and its branches not changed.
  const locked = useReadOnly();
  const nameOf = (id: string) => family.trips.find((trip) => trip.id === id)?.name ?? '…';
  // Where this trip may meet another again: any stop of another trip of the family.
  const meetings = family.trips
    .filter((trip) => trip.id !== plan.trip.id)
    .flatMap((trip) =>
      trip.stops.map((stop) => ({
        value: stop.id,
        label: `${trip.name} · ${stop.city} · ${days(stop.start, stop.end, lang)}`,
      })),
    );
  const kin = (meets: string | null, shared: boolean) => {
    setFailed(false);
    setKin(plan.trip.id, { meets, shared }).then(onFamily, () => {
      setFailed(true);
    });
  };

  return (
    <section className="family" aria-labelledby={`${ids}-title`}>
      <h2 id={`${ids}-title`} className="visually-hidden">
        {t('family.title')}
      </h2>
      {family.trips.length === 1 && <p className="quiet">{t('family.alone')}</p>}
      <ul className="family-tree">
        {family.trips.map((trip) => {
          const { kin: colour, depth } = kinOf(family, trip.id);
          return (
            <li
              key={trip.id}
              className={`family-trip kin-${String(colour)}`}
              style={{ paddingLeft: depth * 16 }}
            >
              <span className="family-name">
                <span className="kin-dot" aria-hidden="true" />
                {trip.id === plan.trip.id || trip.role === null ? (
                  <strong>{trip.name}</strong>
                ) : (
                  <Link to={`/trips/${trip.id}`}>{trip.name}</Link>
                )}
                <span className="quiet">
                  {' · '}
                  {t('trips.days', { count: trip.days })} ·{' '}
                  {t('family.peopleCount', { count: trip.people })}
                </span>
                {trip.budget && (
                  <span className="mono family-budget">{formatWhole(trip.budget, lang)}</span>
                )}
              </span>
              <span className="quiet">
                {days(trip.start, trip.end, lang)}
                {trip.partsAt && ` · ${t('family.partsFrom', { city: trip.partsAt.city })}`}
              </span>
              {trip.meets && (
                <span className="family-meets">
                  ↪{' '}
                  {t('family.meets', {
                    name: nameOf(trip.meets.trip),
                    city: trip.meets.stop
                      ? `${trip.meets.stop.city} ${dayShort(joinsOn(trip.meets.stop.start, trip.end), lang)}`
                      : '…',
                  })}
                </span>
              )}
              {meetNote(trip) !== null && <span className="family-warn">{meetNote(trip)}</span>}
            </li>
          );
        })}
      </ul>
      {me?.parent && !locked && (
        <div className="family-own">
          <Pick
            label={t('family.meetsAgain')}
            options={meetings}
            value={me.meets?.stop?.id ?? null}
            placeholder={t('family.meetsNone')}
            onChange={(stop) => {
              kin(stop, me.shared);
            }}
            onClear={() => {
              kin(null, me.shared);
            }}
          />
          <label className="check">
            <input
              type="checkbox"
              checked={me.shared}
              onChange={(event) => {
                kin(me.meets?.stop?.id ?? null, event.target.checked);
              }}
            />
            {t('family.shared')}
          </label>
        </div>
      )}
      {failed && (
        <p className="form-failed" role="alert">
          {t('plan.failed')}
        </p>
      )}
      {plan.stops.length > 0 && !locked && (
        <button type="button" className="button button-small" onClick={onBranch}>
          {t('family.new')}
        </button>
      )}
    </section>
  );
}

/** One row of the family's rail: a trip's head, or one of its stops. */
export interface RailRow {
  trip: Kin;
  lane: number;
  stop: Kin['stops'][number];
  /** The trips joining it at this stop. */
  joins: Kin[];
}

/** A trip's line on the rail: from where it comes in to its last stop, and where it meets. */
export interface RailLine {
  trip: Kin;
  lane: number;
  /** Where it comes in: this trip's own rail over the rows (row -1), or its parent's lane. */
  from: { lane: number; row: number };
  first: number;
  last: number;
  /** The row and lane of the stop it joins another trip at. */
  meets: { lane: number; row: number } | null;
}

/**
 * The family's other trips as one rail under this one's stops (the Branches mockup, §2): each
 * a lane of its own in its colour, its stops among the others' by day, a head where it comes in
 * and again where it goes on after another's; a branch curves off where it parts and back where
 * it meets another again.
 */
export function railOf(plan: Plan, family: Family): { rows: RailRow[]; lines: RailLine[] } {
  // Only its own branches: a trip's view is the trip and what parts from it.
  const shown = branchesOf(family, plan.trip.id)
    // The one over first goes first, on the lane nearest: a short way home before a long tour.
    .toSorted((a, b) => a.end.localeCompare(b.end) || a.place - b.place);
  // The one going on longest keeps the rail it parts from, as the mockup draws it; the shorter
  // ones curve off to the lanes beside it.
  const lanes = shown.toSorted((a, b) => b.end.localeCompare(a.end) || a.place - b.place);
  const laneOf = (id: string) => lanes.findIndex((trip) => trip.id === id);
  // Each trip's stops kept together, as the mockup has them; one that meets another shown comes
  // just before the stop it joins at, the other going on after it.
  const joiner = (trip: Kin) =>
    shown.some(
      (other) =>
        other.id !== trip.id && other.stops.some((stop) => stop.id === trip.meets?.stop?.id),
    );
  // A branch of a trip shown comes right after the stop it parts at, or after that trip's end.
  const nested = (trip: Kin) =>
    !joiner(trip) && shown.some((other) => other.id === trip.parent);
  const offOf = (trip: Kin, at: string | null) =>
    shown.filter(
      (other) =>
        other.parent === trip.id &&
        nested(other) &&
        (at === null
          ? !trip.stops.some((stop) => stop.id === other.partsAt?.id)
          : other.partsAt?.id === at),
    );
  const block = (trip: Kin): { trip: Kin; stop: Kin['stops'][number] }[] => [
    ...trip.stops.flatMap((stop) => [
      ...shown
        .filter((other) => other.meets?.stop?.id === stop.id && joiner(other))
        .flatMap(block),
      { trip, stop },
      ...offOf(trip, stop.id).flatMap(block),
    ]),
    ...offOf(trip, null).flatMap(block),
  ];
  const stops = shown.filter((trip) => !joiner(trip) && !nested(trip)).flatMap(block);
  // A branch's first stop, of no days in the town it parts at, is where its line curves off:
  // not a row of its own.
  const setOut = (trip: Kin, stop: Kin['stops'][number]) =>
    stop.days === 0 && trip.stops[0]?.id === stop.id && stop.city === trip.partsAt?.city;
  const rows: RailRow[] = stops
    .filter(({ trip, stop }) => !setOut(trip, stop))
    .map(({ trip, stop }) => ({
      trip,
      lane: laneOf(trip.id),
      stop,
      joins: family.trips.filter((other) => other.meets?.stop?.id === stop.id),
    }));
  const rowsOf = (id: string) => rows.flatMap((row, at) => (row.trip.id === id ? [at] : []));
  const lines = shown.flatMap((trip): RailLine[] => {
    const own = rowsOf(trip.id);
    const [first] = own;
    const last = own.at(-1);
    if (first === undefined || last === undefined) return [];
    const parentId = String(trip.parent);
    const parent = laneOf(parentId);
    const target = rows.findIndex((row) => row.stop.id === trip.meets?.stop?.id);
    const into = rows[target];
    return [
      {
        trip,
        lane: laneOf(trip.id),
        // Off this trip, it comes from its rail above; off one of its branches, from that one's
        // lane where it last was.
        from:
          trip.parent === plan.trip.id
            ? { lane: 0, row: -1 }
            : { lane: parent, row: Math.max(...rowsOf(parentId).filter((row) => row < first)) },
        first,
        last,
        meets: into ? { lane: into.lane, row: target } : null,
      },
    ];
  });
  return { rows, lines };
}

/**
 * What this trip's stops say of its family, by stop: `3 trips from here` where its branches part,
 * `Vietnam loop joins 29.03` where another trip joins it.
 */
export function stopNotes(
  family: Family,
  trip: string,
  say: { parts: (count: number) => string; joins: (name: string, day: string) => string },
): Record<string, string> {
  const notes = new Map<string, string[]>();
  const add = (stop: string, note: string) =>
    notes.set(stop, [...(notes.get(stop) ?? []), note]);
  const parting = new Map<string, number>();
  for (const kin of family.trips) {
    if (kin.parent === trip && kin.partsAt) {
      parting.set(kin.partsAt.id, (parting.get(kin.partsAt.id) ?? 0) + 1);
    }
  }
  for (const [stop, count] of parting) add(stop, say.parts(count));
  // Another trip joining this one at its stop: who, and the day they get there.
  for (const kin of family.trips) {
    const stop = kin.meets?.stop;
    if (kin.meets?.trip === trip && stop) {
      add(stop.id, say.joins(kin.name, dotted(joinsOn(stop.start, kin.end))));
    }
  }
  return Object.fromEntries([...notes].map(([stop, said]) => [stop, said.join(' · ')]));
}

/** `29.03`: a day as the rail says it, short. */
function dotted(day: string): string {
  return `${day.slice(8, 10)}.${day.slice(5, 7)}`;
}

/** What can be said cancelled at a stop: a way by how it goes, a ticket, a room. */
const CANCELLED = ['flight', 'train', 'bus', 'ferry', 'car', 'ticket', 'stay'] as const;

/** How far apart the rail's lanes are, how high a row is, and where the first lane is. */
const LANE_GAP = 14;
const RAIL_ROW = 40;
const RAIL_X = 12;

/**
 * The other trips of the family under this one's stops, as one rail with the route's own
 * (the Branches mockup, §2).
 */
export function FamilyRoute({
  plan,
  family,
  picked = null,
  onPick,
}: {
  plan: Plan;
  family: Family;
  /** The stop of another trip picked, standing out. */
  picked?: string | null;
  /** A stop of another trip pressed: shown on the map, this trip staying as it is. */
  onPick?: (trip: string, stop: string) => void;
}) {
  const { t } = useTranslation();
  const { rows, lines } = useMemo(() => railOf(plan, family), [plan, family]);
  if (rows.length === 0) return null;
  const x = (lane: number) => RAIL_X + lane * LANE_GAP;
  const y = (row: number) => row * RAIL_ROW + RAIL_ROW / 2;
  // The rows begin just past the last lane, as a git tree's do.
  const gutter = x(Math.max(...lines.map((line) => line.lane))) + 14;
  const colour = (trip: Kin) => `kin-${String(kinOf(family, trip.id).kin)}`;
  // A stop picked: its trip clear, the others lighter still; none picked, all alike light.
  const pickedTrip = rows.find((row) => row.stop.id === picked)?.trip ?? null;
  // Its people going on with the trip it joins: that trip clear too, from the stop they join it.
  const joinRow = rows.findIndex((row) => row.stop.id === pickedTrip?.meets?.stop?.id);
  const joined = rows[joinRow]?.trip.id ?? null;
  const light = (trip: Kin, at: number) =>
    pickedTrip === null
      ? ''
      : trip.id === pickedTrip.id || (trip.id === joined && at >= joinRow)
        ? ' family-lit'
        : ' family-dim';
  const lastOf = (trip: string) => rows.findLastIndex((row) => row.trip.id === trip);
  const path = (line: RailLine) => {
    // Off this trip's last stop, it starts at that stop's dot just above the rail.
    const fromLast = plan.stops.slice(-1).some((stop) => stop.id === line.trip.partsAt?.id);
    const off = fromLast ? -RAIL_ROW / 2 : 0;
    const top = line.from.row < 0 ? off : y(line.from.row);
    const start = x(line.from.lane);
    const at = y(line.first);
    let d = `M${String(start)} ${String(top)}`;
    if (start !== x(line.lane)) {
      // It curves off right below where it parts, then runs down its own lane.
      const off = Math.min(at, top + RAIL_ROW);
      const mid = (top + off) / 2;
      d += `C${String(start)} ${String(mid)} ${String(x(line.lane))} ${String(mid)} ${String(x(line.lane))} ${String(off)}`;
    }
    d += `V${String(y(line.last))}`;
    if (line.meets) {
      const end = y(line.meets.row);
      const mid = (y(line.last) + end) / 2;
      d += `C${String(x(line.lane))} ${String(mid)} ${String(x(line.meets.lane))} ${String(mid)} ${String(x(line.meets.lane))} ${String(end)}`;
    }
    return d;
  };
  return (
    <div className="family-route" role="group" aria-label={t('family.rail')}>
      {/* The trip's own stops are what it is about: the others are light beside them. */}
      <div className="family-faded">
        <svg
          className="route-rail family-rail"
          width={gutter}
          height={rows.length * RAIL_ROW}
          aria-hidden="true"
        >
          {lines.map((line) => (
            <path
              key={line.trip.id}
              className={`family-rail-line ${colour(line.trip)}${light(line.trip, -1)}`}
              d={path(line)}
            />
          ))}
          {/* The trip joined, clear on from where the picked trip's people join it. */}
          {lines
            .filter((line) => line.trip.id === joined)
            .map((line) => (
              <path
                key={`${line.trip.id}-on`}
                className={`family-rail-line ${colour(line.trip)} family-lit`}
                d={`M${String(x(line.lane))} ${String(y(joinRow))}V${String(y(lastOf(line.trip.id)))}`}
              />
            ))}
          {rows.map((row, at) =>
            // The stop picked a ring in its trip's colour, as the route's own picked stop is;
            // where another joins, a full dot in the colour of the first that does.
            picked === row.stop.id ? (
              <circle
                key={row.stop.id}
                className={`family-rail-picked ${colour(row.trip)}${light(row.trip, at)}`}
                cx={x(row.lane)}
                cy={y(at)}
                r={7}
              />
            ) : row.joins.length > 0 ? (
              row.joins
                .slice(0, 1)
                .map((other) => (
                  <circle
                    key={row.stop.id}
                    className={`family-rail-meet ${colour(other)}${light(row.trip, at)}`}
                    cx={x(row.lane)}
                    cy={y(at)}
                    r={6}
                  />
                ))
            ) : (
              <circle
                key={row.stop.id}
                className={`family-rail-stop ${colour(row.trip)}${light(row.trip, at)}`}
                cx={x(row.lane)}
                cy={y(at)}
                r={5}
              />
            ),
          )}
        </svg>
        <ul className="route-rows family-rows" style={{ paddingLeft: gutter }}>
          {rows.map((row, at) => {
            // Who joins it there is the ring on the rail: its words only the stop's own.
            const name = `${row.stop.city} · ${t('plan.daysShort', { count: row.stop.days })}`;
            const said = (
              <>
                <span className="stop-name" title={name}>
                  {name}
                </span>
                {row.stop.cancelled.map((word, index) => {
                  const known = CANCELLED.find((one) => one === word) ?? 'ticket';
                  const said = t(`family.cancelledWay.${known}`);
                  // How it went, struck through in red: a flight cancelled is a plane crossed out.
                  return (
                    <span
                      key={index}
                      className="cancelled-mark"
                      role="img"
                      aria-label={said}
                      title={said}
                    >
                      <ModeIcon
                        mode={known === 'ticket' || known === 'stay' ? 'travel' : known}
                      />
                    </span>
                  );
                })}
              </>
            );
            return (
              <li
                key={row.stop.id}
                className={`route-row${row.joins.length > 0 ? ' family-meet' : ''}${light(row.trip, at)}`}
              >
                <button
                  type="button"
                  className="stop family-stop"
                  aria-pressed={picked === row.stop.id}
                  onClick={() => {
                    onPick?.(row.trip.id, row.stop.id);
                  }}
                >
                  {said}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/**
 * A stop of another trip of the family, picked on the rail, the timeline or the map: whose it is,
 * when, how it goes on, what was cancelled there; the trip opens from here when it is one's own.
 */
/** A stop's next on its trip; from its last, the stop where it joins another trip, if it does. */
export function nextOf(trip: Kin, stop: Kin['stops'][number]): KinStop | null | undefined {
  const at = trip.stops.findIndex((other) => other.id === stop.id);
  return trip.stops[at + 1] ?? trip.meets?.stop;
}

/** Where a way of another trip goes: its next stop's town, `…` when it goes on to none. */
export const kinTo = (trip: Kin, stop: Kin['stops'][number]) => nextOf(trip, stop)?.city ?? '…';

/**
 * Another trip's stop, picked on the rail or the timeline. On a trip the person is on, it is said
 * as one of this trip's: the stops before and after it, its bookings and estimates, or its way on
 * — from that trip's own `plan`. On one they are not on, what the family says of it: where, how
 * long, how it goes on. Whose stop it is, the widget's bar says in that trip's colour.
 */
export function KinStopDetail({
  family,
  trip,
  stop,
  plan = null,
  way = false,
  onPick,
  weatherDay = null,
}: {
  family: Family;
  trip: Kin;
  stop: Kin['stops'][number];
  /** That trip's plan, when the person is on it. */
  plan?: Plan | null;
  /** Its way on picked, not the stop. */
  way?: boolean;
  /** Picks another stop of that trip, or its way on. */
  onPick?: (stop: string, way: boolean) => void;
  /** The day of its weather to show in full. */
  weatherDay?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const meetsFrom = trip.stops.at(-1)?.id === stop.id ? trip.meets?.stop : undefined;
  const next = nextOf(trip, stop);
  const joining = family.trips.find((other) => other.id === trip.meets?.trip);
  const full = plan?.stops.find((one) => one.id === stop.id);
  const pick = (one: PlanStop, onWay: boolean) => {
    onPick?.(one.id, onWay);
  };
  const meeting = (
    <>
      {meetsFrom && joining && (
        <p className="family-meets">
          ↪{' '}
          {t('family.meets', {
            name: joining.name,
            city: `${meetsFrom.city} ${dotted(joinsOn(meetsFrom.start, trip.end))}`,
          })}
        </p>
      )}
    </>
  );
  if (plan && full) {
    return (
      <div
        className={`kin-stop-full kin-${String(kinOf(family, trip.id).kin)}${way ? ' way-widget' : ''}`}
      >
        <PickedPath
          plan={plan}
          stop={full}
          way={way}
          onStop={(one) => {
            pick(one, false);
          }}
          onWay={(one) => {
            pick(one, true);
          }}
        />
        {meeting}
        {way ? (
          <WayDetail plan={plan} stop={full} />
        ) : (
          <StopDetail plan={plan} stop={full} weatherDay={weatherDay} />
        )}
      </div>
    );
  }
  return (
    // Where it is first, how long, how it goes on.
    <section className={`kin-stop kin-${String(kinOf(family, trip.id).kin)}`}>
      <p className="kin-stop-place">
        <strong>{stop.city}</strong>
        <span className="quiet">{countryName(stop.country, lang)}</span>
      </p>
      <p className="quiet">
        {days(stop.start, stop.end, lang)} · {t('trips.days', { count: stop.days })}
      </p>
      <p className="kin-stop-way">
        {next ? (
          <>
            <ModeIcon mode={stop.mode ?? 'travel'} />{' '}
            {stop.mode ? t(`mode.${stop.mode}`) : t('plan.wayOpen')} · {stop.city} → {next.city}
          </>
        ) : (
          <span className="quiet">{t('family.lastStop')}</span>
        )}
        {stop.cancelled.map((word, index) => (
          <span key={index} className="quiet">
            {' · '}
            {t(`family.cancelledWay.${CANCELLED.find((one) => one === word) ?? 'ticket'}`)}
          </span>
        ))}
      </p>
      {meeting}
    </section>
  );
}
